import { createState, buildTimeline, nearestIndex, ISRAEL } from './state.js';
import { applyStaticLabels } from './i18n.js';
import { fetchRadarFrames } from './data/radar.js';
import { fetchForecastGrid, fetchPointGlance } from './data/forecast.js';
import { createMap, flyToUser, addUserMarker } from './map/basemap.js';
import { ensureRadarLayers, setRadarVisible, hideAllRadar } from './map/radarLayer.js';
import { createForecastOverlay } from './map/forecastOverlay.js';
import {
  bindChrome,
  updateGlance,
  updateTimelineChrome,
  updateLayerButtons,
  updatePlayButton,
} from './ui/chrome.js';

const state = createState();
const stateRef = { current: state };
let map;
let overlay;
let userMarker = null;
let playTimer = 0;
let blend = 0;

async function boot() {
  map = createMap(document.getElementById('map'));
  overlay = createForecastOverlay(map, document.getElementById('overlay'));
  bindChrome(state, {
    toggleLang,
    locate: requestLocate,
    togglePlay,
    setLayer,
    seek,
    pause: () => setPlaying(false),
  });

  map.on('load', async () => {
    const [radar, forecast] = await Promise.all([
      fetchRadarFrames().catch((e) => {
        console.warn(e);
        return [];
      }),
      fetchForecastGrid().catch((e) => {
        console.warn(e);
        return null;
      }),
    ]);
    state.radar = radar;
    state.forecast = forecast;
    state.nowUnix = Math.floor(Date.now() / 1000);
    state.timeline = buildTimeline(radar, forecast, state.nowUnix);
    state.index = nearestIndex(state.timeline, state.nowUnix);

    if (radar.length) ensureRadarLayers(map, radar);
    renderFrame(true);
    updateTimelineChrome(state);
    overlay.start(stateRef);
    syncWindAnim();
    setPlaying(true);

    requestLocate(false);
    refreshGlance();
  });

  map.on('move', () => overlay.paint(state));
  map.on('zoom', () => overlay.paint(state));

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  // Expose for Playwright perf hooks
  window.__rainIl = {
    getState: () => state,
    seek,
    setLayer,
    toggleLang,
  };
}

function renderFrame(hard = false) {
  const frame = state.timeline[state.index];
  if (!frame) return;
  if (frame.kind === 'radar') {
    setRadarVisible(map, state.radar, frame.radarIndex ?? 0, state.reducedMotion || hard ? 0 : blend);
    // canvas clears itself when not forecast
  } else {
    hideAllRadar(map, state.radar);
  }
  overlay.paint(state);
  updateTimelineChrome(state);
  syncWindAnim();
}

function seek(index) {
  state.index = Math.max(0, Math.min(state.timeline.length - 1, index));
  blend = 0;
  renderFrame(true);
  syncWindAnim();
}

function setLayer(layer) {
  state.layer = layer;
  updateLayerButtons(state);
  syncWindAnim();
  overlay.paint(state);
}

function syncWindAnim() {
  const frame = state.timeline[state.index];
  const need = state.layer === 'wind' && frame?.kind === 'forecast';
  overlay.setAnimating(!!need, stateRef);
}

function toggleLang() {
  state.lang = state.lang === 'he' ? 'en' : 'he';
  localStorage.setItem('rain-il-lang', state.lang);
  applyStaticLabels(state.lang);
  updateGlance(state);
  updateTimelineChrome(state);
}

function setPlaying(on) {
  state.playing = on;
  updatePlayButton(state);
  clearInterval(playTimer);
  if (!on) return;
  const stepMs = state.reducedMotion ? 900 : 550;
  playTimer = window.setInterval(() => {
    if (!state.timeline.length) return;
    const next = (state.index + 1) % state.timeline.length;
    if (!state.reducedMotion) {
      blend = 0;
      const start = performance.now();
      const from = state.index;
      const tick = (now) => {
        const u = Math.min(1, (now - start) / 280);
        blend = u * u * (3 - 2 * u);
        state.index = from;
        renderFrame(false);
        if (u < 1) requestAnimationFrame(tick);
        else {
          blend = 0;
          state.index = next;
          renderFrame(true);
        }
      };
      requestAnimationFrame(tick);
    } else {
      state.index = next;
      renderFrame(true);
    }
  }, stepMs);
}

function togglePlay() {
  setPlaying(!state.playing);
}

function requestLocate(fly = true) {
  if (!navigator.geolocation) {
    state.userLoc = { lat: ISRAEL.center[1], lon: ISRAEL.center[0] };
    refreshGlance();
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      state.userLoc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      if (userMarker) userMarker.remove();
      userMarker = addUserMarker(map, state.userLoc.lat, state.userLoc.lon);
      if (fly) flyToUser(map, state.userLoc.lat, state.userLoc.lon);
      refreshGlance();
    },
    () => {
      state.userLoc = { lat: 32.0853, lon: 34.7818 };
      if (userMarker) userMarker.remove();
      userMarker = addUserMarker(map, state.userLoc.lat, state.userLoc.lon);
      refreshGlance();
    },
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
  );
}

async function refreshGlance() {
  const loc = state.userLoc || { lat: 32.0853, lon: 34.7818 };
  try {
    state.glance = await fetchPointGlance(loc.lat, loc.lon);
  } catch {
    state.glance = { mode: 'unknown' };
  }
  updateGlance(state);
}

boot();
